import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';

import { useCreateReportingLeLens } from '~/hooks/apis/reporting/useGetReportingLeLens';
import useLensPublication from '~/hooks/apis/reporting/useLensPublication';
import { lensWriteFailureMessage } from '~/hooks/apis/reporting/useUpdateReportingLeLensErrors';

import type { Lens, LensResponse } from '~/types/leLens';

import { publicationAudience } from './document/publication';

const MAX_LENS_TITLE_LENGTH = 200;
const MAX_PUBLISHED_COPY_TITLE_LENGTH = 150;
const COPY_SUFFIX = ' — Copy';

export function defaultLensCopyTitle(title: string, maxLength = MAX_LENS_TITLE_LENGTH): string {
  const base = title.trim() || 'Untitled Lens';
  return `${base.slice(0, maxLength - COPY_SUFFIX.length)}${COPY_SUFFIX}`;
}

export default function SaveLensAsDialog({
  onClose,
  onCreated,
  open,
  sourceLens,
  publicationDetail,
}: {
  onClose: () => void;
  onCreated: (lens: Lens) => void;
  open: boolean;
  sourceLens: Lens | null;
  publicationDetail?: LensResponse;
}) {
  const createLens = useCreateReportingLeLens();
  const publication = useLensPublication(sourceLens?.id ?? '');
  const pending = createLens.isPending || publication.isPending;
  const titleLimit = publicationDetail ? MAX_PUBLISHED_COPY_TITLE_LENGTH : MAX_LENS_TITLE_LENGTH;
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState<'private' | 'source'>('private');
  const [error, setError] = useState<string | null>(null);
  const seededSource = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      seededSource.current = null;
      return;
    }
    // Metadata polling must not reset a name or audience the reader is entering.
    if (!sourceLens || seededSource.current === sourceLens.id) return;
    seededSource.current = sourceLens.id;
    setTitle(defaultLensCopyTitle(sourceLens.title, titleLimit));
    setError(null);
    setAudience('private');
  }, [open, sourceLens]);

  const close = () => {
    if (!pending) {
      setError(null);
      onClose();
    }
  };

  const createCopy = async () => {
    const nextTitle = title.trim();
    if (!sourceLens || !nextTitle) {
      return;
    }
    setError(null);
    try {
      if (publicationDetail?.publication) {
        const { lens } = await publication.mutateAsync({
          action: 'saveAs',
          baseVersion: publicationDetail.lens.version,
          lensVersion: sourceLens.version,
          title: nextTitle,
          audience,
          spaceId: audience === 'source' ? (sourceLens.spaceId ?? null) : null,
        });
        onCreated(lens);
        return;
      }
      const { lens } = await createLens.mutateAsync({
        description: sourceLens.description,
        quietFailure: true,
        spec: sourceLens.spec,
        successMessage: 'Lens copied',
        title: nextTitle,
      });
      onCreated(lens);
    } catch (createError) {
      setError(lensWriteFailureMessage(createError, 'create'));
    }
  };

  return (
    <Dialog data-tour="copy-lens-dialog" fullWidth maxWidth="sm" onClose={close} open={open}>
      <DialogTitle>{publicationDetail ? 'Publish as a new report' : 'Duplicate Lens'}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {publicationDetail
            ? `Create a separate report from “${sourceLens?.title}”. The original report and its shared draft stay as they are. Certification does not carry over.`
            : `Create an independent draft from “${sourceLens?.title}”. Its cards, data, filters and layout will be copied; future changes will not affect the original.`}
        </DialogContentText>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <TextField
          autoFocus
          disabled={pending}
          fullWidth
          inputProps={{ maxLength: titleLimit }}
          label="New Lens name"
          onChange={(event) => setTitle(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && title.trim()) {
              event.preventDefault();
              void createCopy();
            }
          }}
          value={title}
        />
        {publicationDetail ? (
          <>
            <TextField
              disabled={pending}
              fullWidth
              label="Who can view"
              onChange={(event) => setAudience(event.target.value as 'private' | 'source')}
              select
              sx={{ mt: 2 }}
              value={audience}
            >
              <MenuItem value="private">Only me</MenuItem>
              {publicationDetail.access.canManageSharing &&
              (publicationDetail.sharing?.audience !== 'company' || publicationDetail.access.canShareCompany) ? (
                <MenuItem value="source">Same audience as this report</MenuItem>
              ) : null}
            </TextField>
            <DialogContentText sx={{ mt: 1 }}>
              {audience === 'private'
                ? 'Home: My reports. Only you can see the new report.'
                : `Home: ${publicationDetail.sharing?.home.kind === 'space' ? publicationDetail.sharing.home.spaceName : 'My reports'}. Audience: ${publicationAudience(publicationDetail.sharing)}.`}
            </DialogContentText>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button disabled={pending} onClick={close}>
          Cancel
        </Button>
        <Button disabled={pending || !title.trim()} onClick={() => void createCopy()} variant="contained">
          {pending ? 'Saving…' : publicationDetail ? 'Publish new report' : 'Duplicate Lens'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
